import { State, Action, StateContext, Selector } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { Profile } from 'src/app/models/profile.model';
import { AccountService } from 'src/app/services/apis/account.service';
import {
  LoadProfile,
  LoadProfileSuccess,
  LoadProfileFail,
  UpdateProfile,
  UpdateProfileSuccess,
  UpdateProfileFail,
} from '../actions/profile.actions';
import { tap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';

export interface ProfileStateModel {
  profile: Profile | null;
  loaded: boolean;
  error: string | null;
}

@State<ProfileStateModel>({
  name: 'profile',
  defaults: {
    profile: null,
    loaded: false,
    error: null,
  },
})
@Injectable()
export class ProfileState {
  constructor(private accountService: AccountService) {}

  // Selectors
  @Selector()
  static profile(state: ProfileStateModel): Profile | null {
    return state.profile;
  }

  @Selector()
  static loaded(state: ProfileStateModel): boolean {
    return state.loaded;
  }

  @Selector()
  static error(state: ProfileStateModel): string | null {
    return state.error;
  }

  // Actions
  @Action(LoadProfile)
  loadProfile(ctx: StateContext<ProfileStateModel>) {
    return this.accountService.getProfile().pipe(
      tap((profile) => {
        ctx.dispatch(new LoadProfileSuccess(profile));
      }),
      catchError((error) => {
        ctx.dispatch(new LoadProfileFail(error));
        return of(error);
      })
    );
  }

  @Action(LoadProfileSuccess)
  loadProfileSuccess(
    ctx: StateContext<ProfileStateModel>,
    action: LoadProfileSuccess
  ) {
    ctx.patchState({
      profile: action.profile,
      loaded: true,
      error: null,
    });
  }

  @Action(LoadProfileFail)
  loadProfileFail(
    ctx: StateContext<ProfileStateModel>,
    action: LoadProfileFail
  ) {
    ctx.patchState({
      error: action.error,
      loaded: false,
    });
  }

  @Action(UpdateProfile)
  updateProfile(ctx: StateContext<ProfileStateModel>, action: UpdateProfile) {
    return this.accountService.updateProfile(action.update).pipe(
      tap(() => {
        ctx.dispatch(new UpdateProfileSuccess('Profile update successful'));
      }),
      catchError((error) => {
        ctx.dispatch(new UpdateProfileFail(error));
        return of(error);
      })
    );
  }

  @Action(UpdateProfileSuccess)
  updateProfileSuccess(
    ctx: StateContext<ProfileStateModel>,
    action: UpdateProfileSuccess
  ) {
    ctx.patchState({
      error: null,
    });
  }

  @Action(UpdateProfileFail)
  updateProfileFail(
    ctx: StateContext<ProfileStateModel>,
    action: UpdateProfileFail
  ) {
    ctx.patchState({
      error: action.error,
    });
  }
}
